import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, first } from "rxjs";
import { IIndicator } from "@gtmhub/error-handling";
import { TracingService } from "@webapp/core/tracing/services/tracing.service";
import { SearchFacetsOptions, SearchFacetsOptionsEnum } from "@webapp/search/models/search.models";
import { GlobalSearchMediatorService } from "@webapp/search/services/global-search.mediator.service";

@UntilDestroy()
@Component({
  selector: "global-search",
  templateUrl: "./global-search.component.html",
  styleUrls: ["./global-search.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @Input() public facet: SearchFacetsOptions;

  protected currentFacet$: Observable<SearchFacetsOptions>;
  public searchTerm: string;
  protected readonly facetOptions$: Observable<SearchFacetsOptions[]>;
  protected gettingSearchData: IIndicator = null;
  public isFacetMenuOpen: boolean;
  public isSearchFocused = false;
  public shouldShowPopover = false;
  private clickSubscription: () => void;

  @ViewChild("globalSearch") private globalSearch: ElementRef;
  @ViewChild("resultsPopover") private resultsPopover: ElementRef;
  @ViewChild("facetMenu") private facetMenu: ElementRef;
  @ViewChild("searchInput") private searchInput: ElementRef;

  constructor(
    private globalSearchMediatorService: GlobalSearchMediatorService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private tracingService: TracingService
  ) {
    this.facetOptions$ = this.globalSearchMediatorService.facetToggleOptions$;
    this.globalSearchMediatorService.searchTerm$.pipe(untilDestroyed(this), first()).subscribe((searchTerm) => {
      this.searchTerm = searchTerm;
    });
  }

  public ngOnDestroy(): void {
    if (this.clickSubscription) this.clickSubscription();
  }

  public ngOnInit(): void {
    this.globalSearchMediatorService.setCurrentFacet(this.facet || SearchFacetsOptionsEnum.Everywhere, { skipLoadingRecentItemsOnce: true });
    this.currentFacet$ = this.globalSearchMediatorService.currentFacet$;
    this.globalSearchMediatorService.indicators$.pipe(untilDestroyed(this)).subscribe((indicators) => {
      this.gettingSearchData = indicators.gettingSearchData;
      this.cdr.detectChanges();
    });
  }

  protected onSearchClick(): void {
    if (!this.searchTerm) this.globalSearchMediatorService.loadRecentItems();
    this.isSearchFocused = true;
    this.shouldShowPopover = true;

    if (!this.clickSubscription) {
      this.subscribeForClickOutside();
    }
    this.cdr.markForCheck();
    this.tracingService.traceAction("gs__focused_search_field", () => {});
    window.setTimeoutOutsideAngular(() => {
      this.searchInput.nativeElement.focus();
    });
  }

  public onSearchTermChange(): void {
    this.globalSearchMediatorService.setSearchTerm(this.searchTerm);
  }

  public changeFacet(facet: SearchFacetsOptions): void {
    this.isFacetMenuOpen = false;
    this.globalSearchMediatorService.setCurrentFacet(facet);
    this.cdr.markForCheck();
  }

  public calculateMaxHeight(): number {
    // Top-nav-bar is 45px height, and we have 10px more for better look
    return window.innerHeight - 55;
  }

  private subscribeForClickOutside(): void {
    this.clickSubscription = this.renderer.listen("window", "click", (e: Event) => {
      if (
        this.isSearchFocused &&
        !this.globalSearch.nativeElement.contains(e.target) &&
        !this.resultsPopover?.nativeElement.contains(e.target) &&
        !this.facetMenu?.nativeElement.contains(e.target)
      ) {
        this.isSearchFocused = false;
        this.clickSubscription();
        this.clickSubscription = null;
        this.cdr.markForCheck();
      }
    });
  }
}
